import { usePageHeader } from "@hooks/use_page_header";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  LuAlertCircle,
  LuBanknote,
  LuCheckCircle2,
  LuChevronDown,
  LuChevronUp,
  LuClock,
  LuCopy,
  LuReceipt,
  LuRefreshCw,
  LuUpload,
  LuXCircle,
} from "react-icons/lu";
import copy from "copy-to-clipboard";
import moment from "moment";
import { Button, Checkbox, Modal, message } from "antd";
import { toRp } from "@utils/helper";
import { PopupConfirm } from "@components/general/popup";
import { NoticeType } from "antd/es/message/interface";
import { useMutation } from "@tanstack/react-query";
import { FinanceService } from "@services/finance.service";
import { DetailRequestTagihanVA } from "@services/dtos/finance.dto";
import { linkPage } from "@utils/router";

const statusConfig = {
  PENDING: {
    icon: <LuAlertCircle className="tw-text-2xl tw-text-[#d97706]" />,
    label: "Menunggu Pembayaran",
    bg: "#fffbeb",
    text: "#d97706",
    ring: "#fde68a",
  },
  ONHOLD: {
    icon: <LuClock className="tw-text-2xl tw-text-[#166534]" />,
    label: "Menunggu Konfirmasi",
    bg: "#f0fdf4",
    text: "#166534",
    ring: "#bbf7d0",
  },
  EXPIRED: {
    icon: <LuXCircle className="tw-text-2xl tw-text-[#ef4444]" />,
    label: "Dibatalkan",
    bg: "#fef2f2",
    text: "#ef4444",
    ring: "#fecaca",
  },
  COMPLETED: {
    icon: <LuCheckCircle2 className="tw-text-2xl tw-text-[#22c55e]" />,
    label: "Pembayaran Berhasil",
    bg: "#f0fdf4",
    text: "#22c55e",
    ring: "#bbf7d0",
  },
  REJECTED: {
    icon: <LuXCircle className="tw-text-2xl tw-text-[#ea580c]" />,
    label: "Bukti Ditolak",
    bg: "#fff7ed",
    text: "#ea580c",
    ring: "#fed7aa",
  },
};

type InfoRowProps = {
  label: string;
  value: React.ReactNode;
};

const InfoRow = ({ label, value }: InfoRowProps) => (
  <div className="tw-flex tw-justify-between tw-items-center tw-py-2.5">
    <span className="tw-text-xs tw-text-text-secondary tw-w-2/5">{label}</span>
    <span className="tw-text-sm tw-font-semibold tw-text-text-primary tw-text-right tw-w-3/5">
      {value}
    </span>
  </div>
);

const Divider = () => (
  <div className="tw-h-px" style={{ background: "#f1f1f1" }} />
);

export const VaDetailPage = () => {
  const { state } = useLocation();
  usePageHeader("Detail VA", { bottomNavigation: "hidden" });
  const [messageApi, contextHolder] = message.useMessage();
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [showMessageText, setShowMessageText] = useState<string>("");
  const [messageType, setMessageType] = useState<string>("");
  const [trxID, setTrxID] = useState<string>("");
  const [popupTitle, setPopupTitle] = useState<string>("");
  const [popupMessage, setPopupMessage] = useState<string>("");
  const [popupTextConfirm, setPopupTextConfirm] = useState<string>("");
  const [popupCancel, setPopupCancel] = useState<boolean>(false);
  const [status, setStatus] = useState<string>(state?.vaStatus);
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [buktiFile, setBuktiFile] = useState<File | null>(null);
  const [popupConfirmPayment, setPopupConfirmPayment] =
    useState<boolean>(false);
  const [popupCancelOnhold, setPopupCancelOnhold] = useState<boolean>(false);
  const [checkedNotTransferred, setCheckedNotTransferred] =
    useState<boolean>(false);

  const cfgBase =
    statusConfig[status as keyof typeof statusConfig] ?? statusConfig.EXPIRED;
  const cfg =
    status === "ONHOLD" && state?.vaRejectMessage
      ? {
          ...cfgBase,
          icon: <LuXCircle className="tw-text-2xl tw-text-[#ea580c]" />,
          label: "Bukti Ditolak",
          bg: "#fff7ed",
          text: "#ea580c",
          ring: "#fed7aa",
        }
      : cfgBase;

  const totalAmount =
    state?.vaTrxAmount === 0
      ? (state?.vaBankFee ?? 0)
      : (state?.vaTrxAmount ?? 0);

  const nominalAmount =
    state?.vaBankName === "BSI VA"
      ? (state?.vaTrxAmount ?? 0)
      : (state?.vaTrxAmount ?? 0) - (state?.vaBankFee ?? 0);

  const detailCount =
    (state.detail?.tagihan?.length ?? 0) +
    (state.detail?.uangsaku != "0" ? 1 : 0) +
    (state.detail?.infaq != "0" ? 1 : 0);

  const { mutate, isPending } = useMutation({
    mutationFn: FinanceService.virtualAccountCancel,
    onSuccess: (response) => {
      if (response.success) {
        setStatus("EXPIRED");
        setTrxID("");
        setShowMessage(true);
        setShowMessageText("Pembayaran berhasil dibatalkan!");
        setMessageType("success");
      } else {
        setShowMessage(true);
        setShowMessageText(response.message);
        setMessageType("error");
      }
    },
    onError: (error: any) => {
      setShowMessage(true);
      setShowMessageText(error.message ?? "Internal Error");
      setMessageType("error");
    },
  });

  const { mutate: mutateUpload, isPending: isPendingUpload } = useMutation({
    mutationFn: FinanceService.virtualAccountUploadBukti,
    onSuccess: (response) => {
      if (response.success) {
        setBuktiFile(null);
        setShowMessage(true);
        setShowMessageText("Bukti pembayaran berhasil dikirim!");
        setMessageType("success");
      } else {
        setShowMessage(true);
        setShowMessageText(response.message);
        setMessageType("error");
      }
    },
    onError: (error: any) => {
      setShowMessage(true);
      setShowMessageText(error.message ?? "Internal Error");
      setMessageType("error");
    },
  });

  const { mutate: mutateConfirm, isPending: isPendingConfirm } = useMutation({
    mutationFn: FinanceService.virtualAccountConfirm,
    onSuccess: (response) => {
      if (response.success) {
        setStatus("ONHOLD");
        setPopupConfirmPayment(false);
        setShowMessage(true);
        setShowMessageText(
          "Pembayaran dikonfirmasi, menunggu verifikasi admin.",
        );
        setMessageType("success");
      } else {
        setPopupConfirmPayment(false);
        setShowMessage(true);
        setShowMessageText(response.message);
        setMessageType("error");
      }
    },
    onError: (error: any) => {
      setPopupConfirmPayment(false);
      setShowMessage(true);
      setShowMessageText(error.message ?? "Internal Error");
      setMessageType("error");
    },
  });

  const { mutate: mutateCheckStatus, isPending: isPendingCheckStatus } =
    useMutation({
      mutationFn: FinanceService.virtualAccountCheckStatus,
      onSuccess: (response) => {
        setShowMessage(true);
        // Backend hanya mengembalikan status final (COMPLETED/REJECTED) sebagai
        // string di `data`; selain itu `data` selalu [] dan `success` selalu false.
        if (
          response.success &&
          typeof response.data === "string" &&
          response.data
        ) {
          const newStatus = response.data;
          const newLabel =
            statusConfig[newStatus as keyof typeof statusConfig]?.label ??
            newStatus;
          setMessageType("success");
          if (newStatus !== status) {
            setStatus(newStatus);
            setShowMessageText(`Status diperbarui: ${newLabel}`);
          } else {
            setShowMessageText(`Status belum berubah: ${newLabel}`);
          }
        } else {
          setMessageType("info");
          setShowMessageText(
            "Status belum berubah, masih menunggu konfirmasi.",
          );
        }
      },
      onError: (error: any) => {
        setShowMessage(true);
        setMessageType("error");
        setShowMessageText(error.message ?? "Internal Error");
      },
    });

  const handleConfirm = () => {
    setPopupCancel(false);
    mutate(trxID);
  };

  const onOpenPopupCancelOnhold = (id: string) => {
    setTrxID(id);
    setCheckedNotTransferred(false);
    setPopupCancelOnhold(true);
  };

  const handleConfirmCancelOnhold = () => {
    setPopupCancelOnhold(false);
    mutate(trxID);
  };

  const onCopy = (text: string | number, label: string) => {
    copy(String(text));
    setShowMessage(true);
    setShowMessageText(`${label} berhasil disalin!`);
    setMessageType("success");
  };

  const onOpenPopupCancel = (id: string) => {
    setTrxID(id);
    setPopupTitle("Batalkan Pembayaran");
    setPopupMessage(
      "Jika dibatalkan maka status transaksi menjadi EXPIRED dan TIDAK BISA DIGUNAKAN KEMBALI",
    );
    setPopupTextConfirm("Ya, Batalkan");
    setPopupCancel(true);
  };

  useEffect(() => {
    if (showMessage) {
      messageApi.open({
        type: messageType as NoticeType,
        content: <>{showMessageText}</>,
      });
    }
    setShowMessage(false);
  }, [showMessage, showMessageText, messageType, messageApi]);

  return (
    <>
      {contextHolder}
      <div className="tw-px-4 tw-pt-2 tw-pb-20 tw-space-y-3">
        {/* Status + Total hero card */}
        <div className="card-body tw-text-center tw-py-6">
          <div
            className="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-rounded-full tw-mb-4"
            style={{ background: cfg.bg, border: `1px solid ${cfg.ring}` }}
          >
            {cfg.icon}
            <span
              className="tw-text-xs tw-font-semibold"
              style={{ color: cfg.text }}
            >
              {cfg.label}
            </span>
          </div>

          <div className="tw-text-3xl tw-font-bold tw-text-text-primary tw-mb-1">
            {toRp(totalAmount)}
          </div>
          <div className="tw-text-xs tw-text-text-secondary">
            {status === "COMPLETED"
              ? moment(state.vaDatePayment ?? Date.now()).format(
                  "dddd, DD MMM YYYY · HH:mm",
                )
              : moment(state.vaDateExpired ?? Date.now()).format(
                  "dddd, DD MMM YYYY · HH:mm",
                )}
          </div>
        </div>

        {/* Reject Message */}
        {state?.vaRejectMessage && (
          <div
            className="tw-rounded-2xl tw-px-4 tw-py-3 tw-flex tw-items-start tw-gap-3"
            style={{
              background: "#fff5f5",
              border: "1px solid #fecaca",
              borderLeft: "3px solid #ef4444",
            }}
          >
            <LuAlertCircle
              className="tw-shrink-0 tw-mt-0.5"
              style={{ color: "#ef4444", fontSize: 18 }}
            />
            <div>
              <div className="tw-text-xs tw-font-bold tw-text-red-500 tw-mb-0.5">
                Alasan Penolakan
              </div>
              <div className="tw-text-sm tw-text-text-primary tw-leading-snug">
                {state.vaRejectMessage}
              </div>
            </div>
          </div>
        )}

        {/* VA Number card */}
        <div className="card-body">
          <div className="tw-text-xs tw-font-bold tw-text-text-secondary tw-uppercase tw-tracking-widest tw-mb-3">
            Nomor Rekening
          </div>
          <div
            className="tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-px-4 tw-py-3 tw-cursor-pointer"
            style={{ background: "#f0fdf4", border: "1.5px solid #bbf7d0" }}
            onClick={() => onCopy(state?.vaNumber, "Nomor Rekening")}
          >
            <span className="tw-text-base tw-font-bold tw-text-[#15803d] tw-tracking-wider">
              {state?.vaNumber ?? "-"}
            </span>
            <LuCopy className="tw-text-lg tw-text-[#22c55e] tw-shrink-0" />
          </div>
          <div className="tw-mt-2 tw-text-xs tw-text-text-secondary tw-text-center">
            {state.vaBankName} · {state.vaName}
          </div>
        </div>

        {/* Transaction detail card */}
        <div className="card-body">
          <div className="tw-text-xs tw-font-bold tw-text-text-secondary tw-uppercase tw-tracking-widest tw-mb-1">
            Rincian Transaksi
          </div>

          <InfoRow
            label="ID Transaksi"
            value={
              <span className="tw-flex tw-items-center tw-justify-end tw-gap-1">
                <LuReceipt className="tw-text-text-secondary tw-shrink-0" />
                {state.vaTrxId ?? "-"}
              </span>
            }
          />
          <Divider />
          <InfoRow label="Nominal" value={toRp(nominalAmount)} />
          {state?.vaBankName !== "BSI VA" && (
            <>
              <Divider />
              <InfoRow label="Kode Unik" value={toRp(state?.vaBankFee ?? 0)} />
            </>
          )}
          <Divider />
          <div className="tw-flex tw-justify-between tw-items-center tw-pt-3 tw-pb-1">
            <span className="tw-text-sm tw-font-bold tw-text-text-primary">
              Total
            </span>
            <div
              className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer"
              onClick={() => onCopy(totalAmount, "Nominal Pembayaran")}
            >
              <span className="tw-text-base tw-font-bold tw-text-primary">
                {toRp(totalAmount)}
              </span>
              <LuCopy className="tw-text-base tw-text-primary" />
            </div>
          </div>
          <div className="tw-text-[10px] tw-text-text-secondary tw-text-right">
            ketuk nominal untuk menyalin
          </div>
        </div>

        {/* Detail accordion card */}
        {detailCount > 0 && (
          <div className="card-body">
            <div
              className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer"
              onClick={() => setDetailOpen((p) => !p)}
            >
              <div className="tw-flex tw-items-center tw-gap-2">
                <LuBanknote className="tw-text-primary" />
                <span className="tw-text-sm tw-font-semibold tw-text-text-primary">
                  Detail Tagihan
                </span>
                <span className="tw-text-xs tw-px-1.5 tw-py-0.5 tw-rounded-full tw-bg-primary tw-text-white tw-font-bold">
                  {detailCount}
                </span>
              </div>
              {detailOpen ? (
                <LuChevronUp className="tw-text-text-secondary" />
              ) : (
                <LuChevronDown className="tw-text-text-secondary" />
              )}
            </div>

            {detailOpen && (
              <div className="tw-mt-3 tw-space-y-0">
                {state.detail?.uangsaku != "0" && (
                  <>
                    <Divider />
                    <InfoRow
                      label="Uang Saku"
                      value={toRp(
                        parseFloat(
                          (state.detail.uangsaku ?? "0").replace(",", ""),
                        ),
                      )}
                    />
                  </>
                )}
                {state.detail?.infaq != "0" && (
                  <>
                    <Divider />
                    <InfoRow
                      label="Infaq"
                      value={toRp(
                        parseFloat(
                          (state.detail.infaq ?? "0").replace(",", ""),
                        ),
                      )}
                    />
                  </>
                )}
                {state.detail?.tagihan?.map(
                  (item: DetailRequestTagihanVA, idx: number) => (
                    <div key={idx}>
                      <Divider />
                      <InfoRow
                        label={item.tagihan}
                        value={toRp(
                          parseFloat((item.dibayar ?? "0").replace(",", "")),
                        )}
                      />
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}

        {/* Cek status terbaru dari server */}
        {(status === "PENDING" || status === "ONHOLD") &&
          state.vaBankName !== "BSI VA" && (
            <Button
              loading={isPendingCheckStatus}
              icon={<LuRefreshCw className="tw-text-base" />}
              className="tw-h-11 tw-w-full tw-text-sm tw-font-semibold tw-rounded-xl tw-border tw-border-primary tw-text-primary focus:tw-shadow-none hover:tw-bg-primary/5"
              onClick={() => mutateCheckStatus({ trxID: state?.vaTrxId ?? "" })}
            >
              Cek Status Pembayaran
            </Button>
          )}

        {/* PENDING: Konfirmasi selain BSI VA */}
        {status === "PENDING" && state.vaBankName !== "BSI VA" && (
          <>
            <Button
              loading={isPendingConfirm}
              className="tw-h-11 tw-w-full tw-text-sm tw-font-semibold tw-rounded-xl tw-border-0 tw-text-white focus:tw-shadow-none"
              style={{ background: "#166534" }}
              onClick={() => setPopupConfirmPayment(true)}
            >
              Konfirmasi Pembayaran
            </Button>
          </>
        )}

        {/* PENDING: Konfirmasi + Batalkan */}
        {status === "PENDING" && (
          <>
            {/* <Button
              loading={isPendingConfirm}
              className="tw-h-11 tw-w-full tw-text-sm tw-font-semibold tw-rounded-xl tw-border-0 tw-text-white focus:tw-shadow-none"
              style={{ background: "#166534" }}
              onClick={() => setPopupConfirmPayment(true)}
            >
              Konfirmasi Pembayaran
            </Button> */}
            <Button
              loading={isPending}
              className="tw-h-11 tw-w-full tw-text-sm tw-font-semibold tw-rounded-xl tw-border-red-500 tw-text-red-500 focus:tw-shadow-none hover:tw-bg-red-50"
              onClick={() => onOpenPopupCancel(state?.vaTrxId ?? "")}
            >
              Batalkan Pembayaran
            </Button>
          </>
        )}

        {/* ONHOLD: Batalkan dengan safeguard */}
        {status === "ONHOLD" && state.vaBankName !== "BSI VA" && (
          <Button
            loading={isPending}
            className="tw-h-11 tw-w-full tw-text-sm tw-font-semibold tw-rounded-xl tw-border-red-500 tw-text-red-500 focus:tw-shadow-none hover:tw-bg-red-50"
            onClick={() =>
              state?.vaRejectMessage
                ? onOpenPopupCancel(state?.vaTrxId ?? "")
                : onOpenPopupCancelOnhold(state?.vaTrxId ?? "")
            }
          >
            Batalkan Pembayaran
          </Button>
        )}

        {/* ONHOLD: Upload bukti saja */}
        {/* {status === "ONHOLD" && (
          <>
            <input
              id="bukti-upload"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                e.target.value = "";
                if (!file) return;
                if (!file.type.startsWith("image/")) {
                  setShowMessage(true);
                  setShowMessageText("Hanya file gambar yang diperbolehkan");
                  setMessageType("error");
                  return;
                }
                if (file.size > 1 * 1024 * 1024) {
                  setShowMessage(true);
                  setShowMessageText("Ukuran file tidak boleh lebih dari 1 MB");
                  setMessageType("error");
                  return;
                }
                setBuktiFile(file);
                setShowMessage(true);
                setShowMessageText(`File "${file.name}" dipilih`);
                setMessageType("info");
              }}
            />
            <label htmlFor="bukti-upload" style={{ display: "block" }}>
              <div
                className="tw-h-11 tw-w-full tw-text-sm tw-font-semibold tw-rounded-xl tw-text-white tw-flex tw-items-center tw-justify-center tw-gap-2 tw-cursor-pointer"
                style={{ background: "#166534" }}
              >
                <LuUpload className="tw-text-base" />
                {buktiFile
                  ? `Ganti Bukti (${buktiFile.name})`
                  : "Upload Bukti Transfer"}
              </div>
            </label>
            {buktiFile && (
              <Button
                loading={isPendingUpload}
                className="tw-h-11 tw-w-full tw-text-sm tw-font-semibold tw-rounded-xl tw-border-0 tw-text-white focus:tw-shadow-none"
                style={{ background: "#22c55e" }}
                onClick={() =>
                  mutateUpload({
                    trx_id: state?.vaTrxId ?? "",
                    bukti_transaksi: buktiFile,
                  })
                }
              >
                Kirim Bukti Pembayaran
              </Button>
            )}
          </>
        )} */}
      </div>

      <PopupConfirm
        open={popupCancel}
        title={popupTitle}
        message={popupMessage}
        textConfirm={popupTextConfirm}
        textCancel="Kembali"
        onConfirm={() => handleConfirm()}
        onCancel={() => setPopupCancel(false)}
      />
      <PopupConfirm
        open={popupConfirmPayment}
        title="Konfirmasi Pembayaran"
        message="Pastikan Anda sudah melakukan transfer sesuai nominal yang tertera. Status akan berubah menjadi Menunggu Konfirmasi."
        textConfirm="Ya, Sudah Transfer"
        textCancel="Kembali"
        onConfirm={() => mutateConfirm(state?.vaTrxId ?? "")}
        onCancel={() => setPopupConfirmPayment(false)}
      />

      <Modal
        className="tw-text-center"
        title="Batalkan Pembayaran"
        open={popupCancelOnhold}
        onCancel={() => setPopupCancelOnhold(false)}
        closable={false}
        centered
        footer={[
          <Button
            key="back"
            className="tw-mt-4 tw-m-1"
            style={{ width: "47%" }}
            onClick={() => setPopupCancelOnhold(false)}
          >
            Kembali
          </Button>,
          <Button
            key="submit"
            loading={isPending}
            disabled={!checkedNotTransferred}
            className="tw-mt-4 tw-m-1 tw-text-white"
            style={{
              width: "47%",
              background: checkedNotTransferred ? "#ef4444" : undefined,
            }}
            onClick={handleConfirmCancelOnhold}
          >
            Ya, Batalkan
          </Button>,
        ]}
      >
        <div className="tw-space-y-3 tw-text-left">
          <div
            className="tw-flex tw-items-start tw-gap-2 tw-rounded-xl tw-p-3"
            style={{ background: "#fff7ed", border: "1px solid #fed7aa" }}
          >
            <LuAlertCircle
              className="tw-shrink-0 tw-mt-0.5 tw-text-orange-500"
              style={{ fontSize: 16 }}
            />
            <p className="tw-text-xs tw-text-orange-700 tw-leading-snug tw-m-0">
              <strong>Perhatian:</strong> Jika Anda{" "}
              <strong>sudah melakukan transfer</strong>, jangan batalkan.
              Pembayaran yang sudah masuk tidak dapat dikembalikan secara
              otomatis.
            </p>
          </div>
          <p className="tw-text-sm tw-text-text-secondary">
            Jika dibatalkan, status transaksi menjadi <strong>EXPIRED</strong>{" "}
            dan tidak bisa digunakan kembali.
          </p>
          <Checkbox
            checked={checkedNotTransferred}
            onChange={(e) => setCheckedNotTransferred(e.target.checked)}
          >
            <span className="tw-text-xs tw-text-text-primary">
              Saya <strong>belum</strong> melakukan transfer ke rekening ini
            </span>
          </Checkbox>
        </div>
      </Modal>
    </>
  );
};
